import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { FileInput, FileOutput } from "lucide-react";
const AIFrameworkComparison: React.FC = () => {
  return <div className="py-20 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 relative overflow-hidden">
      {/* Enhanced 3D Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-28 h-28 bg-tagblue/10 rounded-3xl backdrop-blur-sm rotate-12 animate-float" />
        <div className="absolute top-20 right-20 w-20 h-20 bg-tagblue-light/10 rounded-2xl backdrop-blur-sm -rotate-12 animate-float" style={{
        animationDelay: '2s'
      }} />
        <div className="absolute bottom-20 left-20 w-16 h-16 bg-tagblue/15 rounded-xl backdrop-blur-sm rotate-45 animate-float" style={{
        animationDelay: '4s'
      }} />
        <div className="absolute bottom-10 right-10 w-24 h-24 bg-tagblue-light/15 rounded-2xl backdrop-blur-sm -rotate-6 animate-float" style={{
        animationDelay: '1s'
      }} />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-tagblue to-gray-900 text-5xl">
              Our Approach
            </span>
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-xl">
            Comprehensive input and output framework for AI music transparency
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Inputs Card */}
          <div className="group relative">
            {/* Card glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
            
            {/* Main card */}
            <Card className="relative bg-white/80 backdrop-blur-md rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50 group-hover:scale-105 transform h-full">
              <CardContent className="p-10">
                {/* Floating 3D elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-tagblue/20 rounded-xl rotate-45 animate-float opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="flex items-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-tagblue to-tagblue-dark rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                    <FileInput className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 group-hover:text-tagblue transition-colors duration-300">
                    Inputs - Attaching Provenance Data within audio
                  </h3>
                </div>
                
                <div className="text-gray-800">
                  <p className="mb-6 text-lg leading-relaxed">
                    For allowing transparency of pre-model & post-model usage of Music IP, certain 'provenance' metadata needs to be created. Its best to attach it with the file, due to the sensitivity of data and enabling complete trust in this transparency effort. The 5 major categories are:
                  </p>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-tagblue rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">Training datasets (TDM transparency & disclosure)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-tagblue rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">Attribution (transparency ensured to creators)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-tagblue rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">Opt-ins & opt-outs (file based attachments)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-tagblue rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">Ethical Certification (Ensure compliance)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-tagblue rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">Intensional usage reporting (prevent infringements)</span>
                    </li>
                  </ul>
                </div>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
              </CardContent>
            </Card>
          </div>
          
          {/* Outputs Card */}
          <div className="group relative" style={{
          animationDelay: '0.3s'
        }}>
            {/* Card glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
            
            {/* Main card */}
            <Card className="relative bg-white/80 backdrop-blur-md rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50 group-hover:scale-105 transform h-full">
              <CardContent className="p-10">
                {/* Floating 3D elements */}
                <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-tagblue-light/30 rounded-lg -rotate-12 animate-float opacity-0 group-hover:opacity-100 transition-opacity" style={{
                animationDelay: '1s'
              }} />
                
                <div className="flex items-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-tagblue-light to-tagblue rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                    <FileOutput className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 group-hover:text-tagblue transition-colors duration-300">
                    Outputs - Putting QC checks for compliance
                  </h3>
                </div>
                
                <div className="text-gray-800">
                  <p className="mb-6 text-lg leading-relaxed">
                    Once the audio file with provenance data attached moves downstream within the value chain, there are always chance of infringements and bad actors writing wrong metadata. To prevent any quality compromises, we have mapped certain QC tech available to prevent AI misuse:
                  </p>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-tagblue rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">AI Detection (detect & attach provenance)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-tagblue rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">Likeness license (safeguarding voice/personality)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-tagblue rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">Output provenance (Watermarking, Fingerprinting)</span>
                    </li>
                  </ul>
                </div>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-full opacity-60" />
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Enhanced Conclusion Banner */}
        <div className="group relative">
          {/* Card glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-gray-600 to-gray-700 rounded-3xl blur-2xl opacity-25 group-hover:opacity-40 transition-opacity duration-500" />
          
          {/* Main banner */}
          <div className="relative bg-white/90 backdrop-blur-md rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50 group-hover:scale-105 transform">
            {/* Floating 3D elements */}
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-gray-400/20 rounded-lg rotate-45 animate-float opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gray-300/30 rounded-lg -rotate-12 animate-float opacity-0 group-hover:opacity-100 transition-opacity" style={{
            animationDelay: '1s'
          }} />
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-green-600 rounded-full mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-800">
                  <span className="font-bold text-green-700">Scope:</span> Transparency, Disclosures & Copyright Compliance with Tech - wherever Music intersects with AI
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 bg-gradient-to-r from-red-500 to-red-600 rounded-full mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-800">
                  <span className="font-bold text-red-700">Not under the Scope:</span> Licensing deals, royalty models, legislations & legal, Rights management & claims
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default AIFrameworkComparison;